import {
  Box,
  Modal,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseButton from '../../../components/common/BaseButton';
import ModalTitle from '../../../components/common/ModalTitle';
import { Close } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { showSuccessToast } from '../../../utils/toast.jsx';
import { useAuthStore } from '../../../store/auth/useAuthStore';
import useUsersFacade from '../../../store/profile/useUsersFacade';
import { useLocation, useHistory } from 'react-router-dom';

import { TickCircle } from 'iconsax-react';
import { getCookie } from '../../../utils/cookies.js';
import { useConfig } from '../../../Hooks/useConfig.jsx';

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(4),
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 655,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.surface_11
      : '#F5F5F5',
  border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.secondary.neutral_30 : theme.palette.secondary.neutral_90}`,
  padding: theme.spacing(6),
  borderRadius: theme.spacing(4),
  '&:focus': {
    outline: 'none',
  },
  [theme.breakpoints.down('md')]: {
    width: '90%',
    padding: theme.spacing(6),
  },
}));

const CloseIcon = styled(IconButton)(({ theme }) => ({
  fontSize: `${theme.spacing(6)} !important`,
}));

const Description = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(8),
  textAlign: 'left',
  fontSize: '16px !important',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  [theme.breakpoints.down('lg')]: {
    fontSize: '14px !important',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '13px !important',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    marginBottom: theme.spacing(4),
  },
}));

const IconRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '48px',
  padding: '8px 24px',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
  borderRadius: '30px',
  background:
    'linear-gradient(90deg, rgba(183, 222, 193, 0.70) 9.62%, #FAECD4 61.89%, #F6DAAB 100%), #FFF',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    height: '36px',
    padding: '8px 16px',
  },
}));

const IconWrapper = styled(Box)(({ theme, position }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(position === 'start' && { order: 1 }),
  ...(position === 'middle' && {
    order: 2,
    position: 'absolute',
    left: '70%',
    transform: 'translateX(-50%)',
  }),
  ...(position === 'end' && { order: 3 }),
}));

const StyledIcon = styled('img')(({ theme }) => ({
  width: '24px',
  height: '24px',
  [theme.breakpoints.down('md')]: {
    width: '20px',
    height: '20px',
  },
}));

const TextRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
  padding: '0 10px',
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

const TextWrapper = styled(Typography)(({ theme, position }) => ({
  fontSize: '12px !important',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '16px',
  textAlign: 'center',
  color: theme.palette.mode === 'dark' ? '#C6C6C6' : '#787878',
  [theme.breakpoints.down('md')]: {
    fontSize: '10px !important',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '16px',
  },
  ...(position === 'start' && { order: 1 }),
  ...(position === 'middle' && {
    order: 2,
    position: 'absolute',
    left: '72%',
    transform: 'translateX(-50%)',
  }),
  ...(position === 'end' && { order: 3 }),
}));

const DateTextWrapper = styled(Typography)(({ theme, position }) => ({
  fontSize: '12px !important',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '16px',
  textAlign: 'center',
  color: theme.palette.mode === 'dark' ? '#C6C6C6' : '#787878',
  [theme.breakpoints.down('md')]: {
    fontSize: '8px !important',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '16px',
  },
  ...(position === 'start' && { order: 1 }),
  ...(position === 'middle' && {
    order: 2,
    position: 'absolute',
    left: '72%',
    transform: 'translateX(-50%)',
  }),
  ...(position === 'end' && { order: 3 }),
}));

const FeatureList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(4),
  },
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2.5),
}));

const FeatureText = styled(Typography)(({ theme }) => ({
  fontSize: '14px !important',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  color: theme.palette.mode === 'dark' ? '#fff' : '#1A1B1F',
  [theme.breakpoints.down('md')]: {
    fontSize: '11px !important',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
  },
}));

const ModalInnerTitle = styled(Typography)(({ theme }) => ({
  fontSize: `${theme.spacing(7)} !important`,
  fontWeight: 600,
  marginBottom: theme.spacing(8),
  textAlign: 'center',

  [theme.breakpoints.down('xl')]: {
    fontSize: `${theme.spacing(6.25)} !important`,
    marginBottom: theme.spacing(8),
  },

  [theme.breakpoints.down('lg')]: {
    fontSize: `${theme.spacing(6)} !important`,
    marginBottom: theme.spacing(7),
  },

  [theme.breakpoints.down('md')]: {
    fontSize: `${theme.spacing(4.5)} !important`,
    marginBottom: theme.spacing(6),
    fontWeight: 500,
  },
  [theme.breakpoints.down('sm')]: {
    // padding: `0 ${theme.spacing(2)}`
    // width: '80%',
    margin: `0 auto ${theme.spacing(6)}`,
  },
}));

const ModalInnerSubTitle = styled(Typography)(({ theme }) => ({
  fontSize: `${theme.spacing(4)} !important`,
  fontWeight: 500,
  marginBottom: theme.spacing(8),
  textAlign: 'center',

  [theme.breakpoints.down('xl')]: {
    fontSize: `${theme.spacing(4)} !important`,
    marginBottom: theme.spacing(8),
  },

  [theme.breakpoints.down('lg')]: {
    fontSize: `${theme.spacing(6)} !important`,
    marginBottom: theme.spacing(7),
  },

  [theme.breakpoints.down('md')]: {
    fontSize: `${theme.spacing(4.5)} !important`,
    marginBottom: theme.spacing(6),
    fontWeight: 500,
  },
  [theme.breakpoints.down('sm')]: {
    // padding: `0 ${theme.spacing(2)}`
    // width: '80%',
    margin: `0 auto ${theme.spacing(6)}`,
  },
}));

const DoneBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(2),
  backgroundColor: '#DAEEE0',
  width: '100%',
  padding: theme.spacing(2, 3),
  borderRadius: theme.spacing(1.25),
  margin: `0 auto ${theme.spacing(8)}`,
  color: '#389355',
  fontSize: theme.spacing(5),
  fontWeight: 500,

  [theme.breakpoints.down('md')]: {
    fontSize: theme.spacing(3.5),
    fontWeight: 400,
    // width: theme.spacing(51),
    padding: theme.spacing(2, 3),
    margin: `0 auto ${theme.spacing(6)}`,
    // margin: `${theme.spacing(7)} auto ${theme.spacing(7)}`,
  },
}));

const ActivateTrialModal = ({
  open = false,
  handleClose,
  planName = 'Pro',
  onTrialActivated,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const { checkLogin } = useAuthStore();
  const { fetchUsers } = useUsersFacade();
  const location = useLocation();
  const history = useHistory();
  const [trialActivated, setTrialActivated] = useState(false);
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const [dotsCount, setDotsCount] = useState(0);

  const { baseDomain, panelUrl, playgroundUrl, assistantUrl } = useConfig();

  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  const isPanel = subdomain.includes('panel');

  const navigateToPlayground = () => {
    // Import necessary utilities at the top of your component

    // List of cookies to check
    const cookieNames = [
      'profile-data',
      'company-data',
      'resume-review',
      'resume-parser',
      'resume-score',
    ];

    // Check for any of these cookies
    let foundCookie = null;
    for (const cookieName of cookieNames) {
      const cookieValue = getCookie(cookieName, baseDomain);
      if (cookieValue) {
        foundCookie = cookieName;
        break;
      }
    }

    // Determine navigation path based on cookie
    if (foundCookie) {
      if (isPanel) {
        window.location.href = `${playgroundUrl}/${foundCookie}`;
      } else {
        history.push(`/${foundCookie}`);
      }
    } else {
      // Original behavior if no cookie is found
      if (isPanel) {
        window.location.href = `${playgroundUrl}`;
      } else {
        history.push('/');
      }
    }
  };

  useEffect(() => {
    if (trialActivated) {
      const interval = setInterval(() => {
        setDotsCount((prev) => (prev + 1) % 4);
      }, 300);
      return () => clearInterval(interval);
    }
  }, [trialActivated]);

  useEffect(() => {
    if (trialActivated) {
      const timeout = setTimeout(() => {
        navigateToPlayground();
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [trialActivated, history, assistantUrl]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const currentDate = new Date();
  const reminderDate = new Date(currentDate);
  reminderDate.setDate(currentDate.getDate() + 5);
  const endDate = new Date(currentDate);
  endDate.setDate(currentDate.getDate() + 7);

  const handleActivateTrial = async () => {
    try {
      setLoading(true);
      const response = await api.call({
        name: 'activateTrial',
        data: {},
      });

      if (response && !response.response) {
        if (location.pathname !== '/stepper') {
          showSuccessToast('Your trial has been activated successfully!');
        } else {
          setTrialActivated(true);
        }

        // Refresh stores
        checkLogin();
        fetchUsers();

        // Notify parent component that trial was activated
        if (onTrialActivated) {
          onTrialActivated();
        }
        if (location.pathname !== '/stepper') {
          handleClose();
        }
      }
    } catch (error) {
      console.error('Trial activation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      sx={{
        '.MuiModal-backdrop': {
          backgroundColor: 'rgba(27, 27, 31, 0.2)',
          backdropFilter: 'blur(2.5px) ',
        },
      }}
      onClose={handleClose}
    >
      <ModalBox>
        {!trialActivated ? (
          <>
            <HeaderBox>
              <ModalTitle>Confirm your 7-day free trial</ModalTitle>
              <CloseIcon onClick={handleClose}>
                <Close />
              </CloseIcon>
            </HeaderBox>

            <Description variant="body1">
              Access most Features on the professional plan 7 days. you can
              upgrade anytime you want, otherwise you'll be automatically moved
              to the free forever plan.
            </Description>

            <IconRow>
              <IconWrapper position="start">
                <StyledIcon src="/static/img/Lock Icon.svg" alt="Lock Icon" />
              </IconWrapper>
              <IconWrapper position="middle">
                <StyledIcon
                  src="/static/img/Reminder Icon.svg"
                  alt="Reminder Icon"
                />
              </IconWrapper>
              <IconWrapper position="end">
                <StyledIcon src="/static/img/End Icon.svg" alt="End Icon" />
              </IconWrapper>
            </IconRow>

            <TextRow>
              <TextWrapper position="start">Trial Start</TextWrapper>
              <TextWrapper position="middle">Reminder</TextWrapper>
              <TextWrapper position="end">Trial End</TextWrapper>
            </TextRow>
            <TextRow>
              <DateTextWrapper position="start">
                {formatDate(currentDate)}
              </DateTextWrapper>
              <DateTextWrapper position="middle">
                {formatDate(reminderDate)}
              </DateTextWrapper>
              <DateTextWrapper position="end">
                {formatDate(endDate)}
              </DateTextWrapper>
            </TextRow>

            <FeatureList>
              <FeatureItem>
                <img
                  src={
                    theme.palette.mode === 'dark'
                      ? '/static/img/icon/vuesax/Credit Card Icon-dark.svg'
                      : '/static/img/icon/vuesax/linear/card-remove-light.svg'
                  }
                  alt="No Card"
                  style={{ width: '20px', height: '20px' }}
                />
                <FeatureText>No credit card required</FeatureText>
              </FeatureItem>

              <FeatureItem>
                <img
                  src={
                    theme.palette.mode === 'dark'
                      ? '/static/img/icon/vuesax/Credits Icon-dark.svg'
                      : '/static/img/icon/vuesax/linear/vuesax/linear/coin-light.svg'
                  }
                  alt="Cancel"
                  style={{ width: '20px', height: '20px' }}
                />
                <FeatureText>Get 2000 credits</FeatureText>
              </FeatureItem>

              <FeatureItem>
                <img
                  src={
                    theme.palette.mode === 'dark'
                      ? '/static/img/icon/vuesax/IconWrapper-dark.svg'
                      : '/static/img/icon/IconWrapper-light.svg'
                  }
                  alt="Features"
                  style={{ width: '20px', height: '20px' }}
                />
                <FeatureText>Cancel your trial at any time</FeatureText>
              </FeatureItem>
            </FeatureList>

            <BaseButton
              type="button"
              // disabled={loading}
              loading={loading}
              size="medium"
              color="success"
              sx={{
                width: '100%',
                display: 'block',
                ml: 'auto',
                mt: 2,
              }}
              onClick={handleActivateTrial}
            >
              Confirm
            </BaseButton>
          </>
        ) : (
          <>
            <DoneBox>
              <TickCircle size={mdDown ? 20 : 32} />
              Your Free Trial is Activated!
            </DoneBox>
            <ModalInnerTitle>Unlock your Free Trial benefits</ModalInnerTitle>
            <ModalInnerSubTitle>
              Taking you to the playground
              <span
                style={{
                  display: 'inline-block',
                  width: '1.5em',
                  textAlign: 'left',
                }}
              >
                {'.'.repeat(dotsCount)}
              </span>
            </ModalInnerSubTitle>
            <BaseButton
              type="button"
              disabled={true}
              // loading={loading}
              size="medium"
              color="success"
              sx={{
                width: '100%',
                display: 'block',
                ml: 'auto',
                mt: 2,
              }}
              onClick={handleActivateTrial}
            >
              Confirm
            </BaseButton>
          </>
        )}
      </ModalBox>
    </Modal>
  );
};

export default ActivateTrialModal;
